import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  CircularProgress,
  useTheme,
} from "@mui/material"
import { decode } from "html-entities"
import React, { useCallback, useState, useEffect } from "react"
import { useTranslation } from "react-i18next"
import GameItem from "./GameItem"
import MemorySettings from "../games/Memorama/MemorySettings"
import { useNavigate } from "react-router"
import SmashGameSettings from "../games/SmashGame/SmashGameSettings"
import { styles } from "./styles/ChooseGameModal.styles"
import useSound from "use-sound"
import { cardHover } from "utils/sounds"
import {
  ACTIVITY_TYPES,
  ACTIVITY_VIEW_MODES,
  CONTENT_TYPES,
} from "pages/student/activity/common/constants"
import { useGlobalAlert } from "contexts/GlobalAlertContext"
import { useAuthContext } from "contexts/AuthContext"
import { routes } from "common/navigationRoutes"
import WordSearchSettings from "../games/WordSearch/WordSearchSettings"
import PropTypes from "prop-types"
import useApi from "modules/library"
import { isRunningFromIframe } from "common/functions"
import { MAX_WORD_LENGTH, MIN_WORD_LENGTH } from "../games/helpers/contants"
import Swal from "sweetalert2"
import withReactContent from "sweetalert2-react-content"
import Image from "components/Image"
import { formatWordToSearch, parseText } from "../activity/common/functions"

ChooseGameModal.propTypes = {
  visible: PropTypes.bool,
  onClose: PropTypes.func,
  data: PropTypes.object,
  currentPage: PropTypes.number,
  currentSearch: PropTypes.string,
  moduleType: PropTypes.number,
  viewMode: PropTypes.string,
  onGameOpen: PropTypes.func,
  variant: PropTypes.string,
}

export const MODAL_VARIENTS = {
  DEFAULT: "default",
  SIMPLE: "simple",
}

export default function ChooseGameModal({
  visible,
  onClose,
  data = {
    moduleName: "",
  },
  currentPage = 0,
  currentSearch = "",
  moduleType = -1,
  viewMode = ACTIVITY_VIEW_MODES.STUDENT,
  onGameOpen = () => {},
  variant = MODAL_VARIENTS.DEFAULT,
  fontSettings = {},
}) {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { showAlert } = useGlobalAlert()
  const { selectedGroupData, audioSettings } = useAuthContext()
  const [memoramaSettingsOpen, setMemoramaSettingsOpen] = useState(false)
  const [smashGameSettingsOpen, setSmashGameSettingsOpen] = useState(false)
  const [wordSearchSettingsOpen, setWordSearchSettingsOpen] = useState(false)
  const [playHover, { sound }] = useSound(cardHover, {
    interrupt: true,
    volume: 0.5,
    soundEnabled: audioSettings?.enabledAudioEffects,
    html5: false,
  })
  const [isPlaying, setIsPlaying] = useState(false)
  const { getActivityContent, getExercises } = useApi()
  const [hasWordSearchGame, setHasWordSearchGame] = useState(false)
  const [hasEasyWordSearchGame, setHasEasyWordSearchGame] = useState(false)
  const [hasMemoramaGame, setHasMemoramaGame] = useState(false)
  const [hasCrosswordGame, setHasCrosswordGame] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const MySwal = withReactContent(Swal)
  const theme = useTheme()

  useEffect(() => {
    sound?.on("play", () => setIsPlaying(true))
    sound?.on("stop", () => setIsPlaying(false))
    sound?.on("end", () => setIsPlaying(false))
    sound?.on("pause", () => setIsPlaying(false))

    return () => {
      sound?.off("play")
      sound?.off("stop")
      sound?.off("end")
      sound?.off("pause")
    }
  }, [sound])

  useEffect(() => {
    if (!visible) return
    if (!data?.moduleId || !data?.hasMemorama) return
    setIsLoading(true)
    MySwal.fire({
      html: (
        <Stack
          spacing={2}
          justifyContent={"center"}
          alignItems={"center"}
          sx={{
            minWidth: 400,
            minHeight: 180,
          }}
        >
          <CircularProgress
            sx={{
              color: theme.palette.primary.main,
              width: 50,
              height: 50,
            }}
            size={"large"}
          />
          <Typography variant="span" fontSize={20}>
            {t("loading")}
          </Typography>
        </Stack>
      ),
      showCancelButton: false,
      showConfirmButton: false,
    })
    getWords(data.moduleId)
  }, [data?.moduleId, visible])

  const onHover = useCallback(
    (e) => {
      if (isPlaying) return
      playHover()
    },
    [playHover, isPlaying]
  )

  const openSmashGame = useCallback(
    (settings) => {
      onClose()
      onGameOpen()
      if (viewMode === ACTIVITY_VIEW_MODES.TEACHER_ACTIVITY_BASE) {
        openGameForTeacher({})
        return
      }
      const smashURL = window.location.origin
      const runningFromIframe = isRunningFromIframe()
      // if current environment is production, open smash game in new tab
      if (!runningFromIframe) {
        console.log(`${data[settings?.selection]}`)
        window.location.href = `${smashURL}/${
          data[settings?.selection]
        }&option=smashGame`
      } else {
        if (!data[settings?.selection]) {
          const teacherImage = selectedGroupData?.teachers?.[0]?.avatar
          showAlert("Something went wrong", teacherImage)
        } else {
          if (window?.parent?.parentNavigation) {
            window?.parent?.parentNavigation(
              `${data[settings?.selection]}&option=smashGame`
            )
          } else {
            console.log(`${data[settings?.selection]}&option=smashGame`)
          }
        }
      }
    },
    [data, selectedGroupData]
  )

  const openMemoramaGame = useCallback(
    (settings) => {
      onClose()
      onGameOpen()
      if (viewMode === ACTIVITY_VIEW_MODES.TEACHER_ACTIVITY_BASE) {
        openGameForTeacher({
          ...settings,
          route: `${routes.MEMORAMA}?moduleId=${data.moduleId}&cardType=${settings?.cardType}`,
        })
        return
      }
      navigate(routes.MEMORAMA, {
        item: data.moduleId,
        state: {
          moduleId: data.moduleId,
          title: data.moduleName,
          settings,
          headerVisible: false,
          onBack: {
            page: currentPage,
            search: currentSearch,
            moduleType: moduleType,
            moduleId: data.moduleId,
          },
        },
      })
    },
    [data]
  )

  const openWordSearchGame = useCallback(
    async (settings) => {
      onClose()
      onGameOpen()
      if (viewMode === ACTIVITY_VIEW_MODES.TEACHER_ACTIVITY_BASE) {
        const directions = settings?.directions?.join(",")
        openGameForTeacher({
          ...settings,
          route: `${routes.WORD_SEARCH}?moduleId=${data.moduleId}&difficulty=${settings?.difficulty}&directions=${directions}`,
        })
        return
      }
      // navigate to /games/wordsearch and send props to wordsearch game with react router
      navigate(routes.WORD_SEARCH, {
        item: data.moduleId,
        state: {
          moduleId: data.moduleId,
          title: data.moduleName,
          headerVisible: false,
          directions: settings?.directions,
          difficulty: settings?.difficulty,
          onBack: {
            page: currentPage,
            search: currentSearch,
            moduleType: moduleType,
            moduleId: data.moduleId,
          },
        },
      })
    },
    [data]
  )

  const openCrosswordGame = useCallback(async () => {
    onClose()
    onGameOpen()
    if (viewMode === ACTIVITY_VIEW_MODES.TEACHER_ACTIVITY_BASE) {
      openGameForTeacher({
        route: `${routes.CROSSWORDS}?moduleId=${data.moduleId}`,
      })
      return
    }
    navigate(routes.CROSSWORDS, {
      item: data.moduleId,
      state: {
        moduleId: data.moduleId,
        title: data.moduleName,
        headerVisible: false,
        onBack: {
          page: currentPage,
          search: currentSearch,
          moduleType: moduleType,
          moduleId: data.moduleId,
        },
      },
    })
  }, [data])

  const openWordsMazeGame = useCallback(async () => {
    onClose()
    onGameOpen()
    if (viewMode === ACTIVITY_VIEW_MODES.TEACHER_ACTIVITY_BASE) {
      openGameForTeacher({
        route: `${routes.WORDS_EMOJI}?moduleId=${data.moduleId}`,
      })
      return
    }
    navigate(routes.WORDS_EMOJI, {
      item: data.moduleId,
      state: {
        moduleId: data.moduleId,
        title: data.moduleName,
        headerVisible: false,
        onBack: {
          page: currentPage,
          search: currentSearch,
          moduleType: moduleType,
          moduleId: data.moduleId,
        },
      },
    })
  }, [data])

  const openGameForTeacher = useCallback((settings) => {
    const runningFromIframe = isRunningFromIframe()
    if (!runningFromIframe) return
    if (!window?.parent?.onGameSelected) return
    window?.parent?.onGameSelected(JSON.stringify(settings))
  }, [])

  const openSmashGameSettings = useCallback(() => {
    if (viewMode === ACTIVITY_VIEW_MODES.TEACHER_ACTIVITY_BASE) {
      onClose()
      openGameForTeacher({
        route: `/professor/smash/V2/Game?content=${data.moduleId}`,
      })
      return
    }
    setSmashGameSettingsOpen(true)
  }, [data])

  const onCancel = useCallback(() => {
    setIsLoading(false)
    MySwal.close()
    onClose()
    const runningFromIframe = isRunningFromIframe()
    if (
      viewMode === ACTIVITY_VIEW_MODES.TEACHER_ACTIVITY_BASE &&
      runningFromIframe &&
      window?.parent?.onGameSelected
    ) {
      window?.parent?.onGameSelected(null)
    }
  }, [])

  const getWords = useCallback(async (moduleId) => {
    try {
      const response = await getActivityContent({ moduleId })
      if (response.data) {
        const activityWords = response?.data?.activities.filter(
          (item) => item?.activityTypeId === ACTIVITY_TYPES.WORDS_TO_REMEMBER
        )
        if (activityWords.length === 0) {
          setHasWordSearchGame(false)
          setHasMemoramaGame(false)
          setHasCrosswordGame(false)
          setIsLoading(false)
          MySwal.close()
        } else {
          const exercises = await getExercises({
            moduleId,
            activityId: activityWords[0]?.activityId,
          })
          if (exercises.length === 0) {
            setHasWordSearchGame(false)
            setHasMemoramaGame(false)
            setHasCrosswordGame(false)
            setIsLoading(false)
            MySwal.close()
            return
          }
          setHasMemoramaGame(true)
          const exercise = exercises[0]
          const moduleLanguageId = response?.data?.module?.templateLanguageId
          let words = exercise.content
            .filter((content) => content.contentTypeId === CONTENT_TYPES.WORD)
            .map((answer) => {
              const resultWord = formatWordToSearch(
                answer?.text || "",
                moduleLanguageId
              )
              const clueWord = parseText(
                answer?.text
                  ?.replace(/'/g, "")
                  .replace(/[()]/g, "")
                  .replace(/[/]/g, "")
              )
              return {
                text: answer.text,
                answer: resultWord,
                clue: clueWord,
              }
            })
          const crosswordWords = words.filter(
            (word) =>
              word.clue.length > MIN_WORD_LENGTH &&
              word.clue.length < MAX_WORD_LENGTH
          )
          setHasCrosswordGame(crosswordWords.length > 1) // there must be at least 2 words to play crossword
          words = words.filter(
            (word) =>
              word.answer.replace(/ /g, "").length > MIN_WORD_LENGTH &&
              word.answer.replace(/ /g, "").length < MAX_WORD_LENGTH
          )
          // replace all items that have more than 2 words and leave only the first word
          words = words.map((word) => {
            const answer = word.answer.split(" ")
            return {
              ...word,
              answer:
                answer.length >= 2 && answer[0].length > MIN_WORD_LENGTH
                  ? answer[0]?.replace(/ /g, "-")
                  : word.answer?.replace(/ /g, "-"),
            }
          })
          // remove repeated words from the array
          words = words.filter(
            (word, index, self) =>
              index === self.findIndex((t) => t.answer === word.answer)
          )
          setHasWordSearchGame(words.length > 1) // there must be at least 2 words to play word search
          // easy, remove all items that have spaces or dashes
          words = words.filter(
            (word) => !word.answer.includes(" ") && !word.answer.includes("-")
          )
          setHasEasyWordSearchGame(words.length > 1) // there must be at least 2 words to play word search
          // console.log("~ words:", words)
          // console.log(words.length, "~ words:")
          MySwal.close()
        }
      } else {
        setHasCrosswordGame(false)
        setHasMemoramaGame(false)
        setHasWordSearchGame(false)
      }
      setIsLoading(false)
      MySwal.close()
    } catch (error) {
      setIsLoading(false)
      MySwal.close()
    }
  }, [])

  const games = [
    ...(data?.hasMemorama
      ? [
          {
            title: "memorama",
            image: "gameMemorama",
            onClick: () => setMemoramaSettingsOpen(true),
            visible: hasMemoramaGame,
          },
          {
            title: "word_search",
            image: "gameWordSearch",
            onClick: () => setWordSearchSettingsOpen(true),
            visible: hasWordSearchGame,
          },
          {
            title: "crossword",
            image: "gameWordcross",
            onClick: openCrosswordGame,
            visible: hasCrosswordGame,
          },
          {
            title: "words_maze",
            image: "gameWordsMaze",
            onClick: openWordsMazeGame,
            visible: hasWordSearchGame,
          },
        ]
      : []),
    {
      title: "smash_game",
      image: "gameSmash",
      onClick: openSmashGameSettings,
      visible: true,
    },
  ]

  if (!data?.moduleId || isLoading) return null

  return (
    <Dialog
      open={visible}
      onClose={onCancel}
      fullWidth
      PaperProps={{
        sx: [
          styles.modalContainer,
          fontSettings?.fontFamily && {
            "& .MuiTypography-root, button, textarea": {
              fontFamily: fontSettings.fontFamily,
            },
          },
          fontSettings?.bgColor && {
            backgroundColor: fontSettings.bgColor,
          },
        ],
      }}
      hideBackdrop={viewMode === ACTIVITY_VIEW_MODES.TEACHER_ACTIVITY_BASE}
    >
      <MemorySettings
        open={memoramaSettingsOpen}
        onClose={() => setMemoramaSettingsOpen(false)}
        onSuccess={openMemoramaGame}
        title={data?.moduleName}
        item={data}
        fontSettings={fontSettings}
      />
      <SmashGameSettings
        open={smashGameSettingsOpen}
        onClose={() => setSmashGameSettingsOpen(false)}
        onSuccess={openSmashGame}
        title={data?.moduleName}
        item={data}
        fontSettings={fontSettings}
      />
      <WordSearchSettings
        open={wordSearchSettingsOpen}
        onClose={() => setWordSearchSettingsOpen(false)}
        onSuccess={openWordSearchGame}
        title={data?.moduleName}
        item={data}
        easyLevelAvailable={hasEasyWordSearchGame}
        fontSettings={fontSettings}
      />
      {variant === MODAL_VARIENTS.SIMPLE && (
        <Image name="games" sx={styles.mainImage} />
      )}
      {variant === MODAL_VARIENTS.DEFAULT && (
        <DialogTitle
          textAlign={"center"}
          sx={{ backgroundColor: theme.palette.background.default }}
        >
          <Typography
            variant="h2"
            sx={[
              styles.title,
              fontSettings?.fontSize && {
                fontSize:
                  parseInt(fontSettings?.fontSize?.replace("px", "")) + 9,
              },
            ]}
          >
            {data?.moduleName}
          </Typography>
          {data?.templateName && (
            <Typography variant="body" textAlign={"center"}>
              {decode(
                `${data?.templateName}. ${data?.levelName}. ${data?.unitName}`
              )}
            </Typography>
          )}
        </DialogTitle>
      )}
      <DialogContent sx={styles.modalContent}>
        {variant === MODAL_VARIENTS.DEFAULT && (
          <Alert variant="filled" icon={false} sx={styles.alertMessage}>
            {t("Choose the game you want to play")}
          </Alert>
        )}
        <Stack
          // direction={"row"}
          // spacing={1}
          // alignItems={"center"}
          // justifyContent={"center"}
          // pt={2}
          // px={2}
          direction={"row"}
          alignItems={"stretch"}
          justifyContent={"center"}
          flexWrap={"wrap"}
          sx={[
            styles.gamesItemContainer,
            games.filter((g) => g.visible !== false).length > 4 &&
              styles.gamesItemContainerMax,
          ]}
        >
          {games
            .filter((g) => g.visible !== false)
            .map((value, index) => (
              <GameItem
                key={index}
                {...value}
                onHover={onHover}
                variant={variant}
              />
            ))}
        </Stack>
      </DialogContent>
      {variant === MODAL_VARIENTS.DEFAULT && (
        <DialogActions sx={styles.footerButtons}>
          <Button
            variant="contained"
            color="danger"
            disableElevation
            sx={styles.cancelButton}
            onClick={onCancel}
          >
            {t("cancel")}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  )
}
